import { Grid } from 'components/shared/Grid';
import {
  ADD_ACCOUNT,
  ASSIGN_ACCOUNT,
  UNASSIGN_ACCOUNT,
  EDIT_ACCOUNT,
  PERIOD_REORDER,
  REMOVE_ACCOUNT,
  SET_COLUMN_STATE,
  SET_GROUP_STATE,
  SET_GROUPS_EXPANDED,
} from 'constants/actionTypes';
import React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { Tooltip } from 'components/shared/Tooltip';
import accountRowsSelector, {
  checksumRowSelector,
} from 'selectors/accountRows';
import { gridStateSelector } from 'selectors/gridState';
import { periodColumnsSelector, periodIdsSelector } from 'selectors/periods';
import { settingsSelector } from 'selectors/settings';
import {
  isDeepEqual,
  exportCellFormatter,
  copyCellFormatter,
} from 'utils/generic';
import {
  gridComponentDidUpdate,
  handleGridChange,
  handleGridReady,
} from 'utils/grid';
import { rowToAccount } from 'utils/gridToState';
import { ACCOUNTS_PATH, GROUPS_ACCOUNTS_PATH } from 'constants/paths';
import { ACCOUNTS_GRID, CellIds } from 'constants/grid';
import CustomAccountsFilter from './CustomAccountsFilter';
import { accountFilters } from 'constants/filters';

class AccountsGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isDragging: false,
    };

    this.frameworkComponents = {
      tooltip: Tooltip,
      customAccountsFilter: CustomAccountsFilter,
    };
  }

  messagesTooltipValueGetter = ({ data }) =>
    data.messages.length > 0 ? data.messages.join('\n') : '';
  groupTooltipValueGetter = ({ data }) => data.groupPath;

  get columnDefs() {
    return [
      {
        field: 'number',
        colId: 'number',
        cellClass: CellIds.string,
        tooltipComponent: 'tooltip',
        tooltipValueGetter: this.messagesTooltipValueGetter,
        editable: (params) => !params.node.rowPinned,
        dndSource: this.props.draggable,
        dndSourceOnRowDrag: ({ rowNode, dragEvent: e }) => {
          if (!rowNode.selected) {
            // if user grabs unselected row drag handle then
            // deselect all previously selected rows and select only dragged row
            rowNode.gridApi.getSelectedNodes().forEach((node) => {
              node.setSelected(false);
            });
            rowNode.setSelected(true);
          }

          let data = rowNode.gridApi.getSelectedNodes().map((n) => n.data);

          // target element is one of the account rows, use its HTML (style) to mock all other rows by
          // creating an HTML string that will be used as part of drag image
          const targetCellHTML = e.target.parentElement.outerHTML;
          const nodes = data.map((n) =>
            targetCellHTML.replace(rowNode.data.number, n.number)
          );
          const dragImg = document.getElementById('custom-dnd-ghost');
          dragImg.innerHTML = nodes.join('');
          e.dataTransfer.setDragImage(dragImg, 0, 0);

          data = JSON.stringify(data);
          const userAgent = window.navigator.userAgent;
          const isIE = userAgent.indexOf('Trident/') >= 0;
          if (isIE) {
            e.dataTransfer.setData('text', data);
          } else {
            e.dataTransfer.setData('application/json', data);
            e.dataTransfer.setData('text/plain', data);
          }
        },
      },
      {
        field: 'description',
        colId: 'description',
        cellClass: CellIds.string,
        tooltipComponent: 'tooltip',
        tooltipValueGetter: this.messagesTooltipValueGetter,
        editable: (params) => !params.node.rowPinned,
      },
      {
        field: 'group',
        colId: 'group',
        cellClass: CellIds.string,
        tooltipComponent: 'tooltip',
        tooltipValueGetter: this.groupTooltipValueGetter,
      },
      ...this.props.periodColumns,
    ];
  }

  componentDidUpdate(prevProps) {
    const {
      periodIds,
      colState,
      setColumnState,
      setGroupState,
      path,
      activeFilter,
    } = this.props;

    const havePeriodsChanged = !isEqual(prevProps.periodIds, periodIds);
    if (havePeriodsChanged) {
      gridComponentDidUpdate(
        colState,
        setColumnState,
        setGroupState,
        path,
        ACCOUNTS_GRID,
        this.columnApi
      );
    }

    if (prevProps.activeFilter !== activeFilter) {
      this.gridApi.onFilterChanged();
    }
  }

  getToolPanelItems = () => {
    const { activeFilter } = this.props;

    const hasActiveFilter = !!activeFilter;
    const currentFilter = hasActiveFilter ? activeFilter : accountFilters.ALL;
    const labelDefault = `Filter - ${currentFilter}`;

    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'customAccountsFilter',
          labelDefault,
          labelKey: 'customAccountsFilter',
          toolPanel: 'customAccountsFilter',
        },
      ],
      position: 'right',
    };
  };

  processCellForClipboard = (params) => {
    const { settings } = this.props;

    const cellFormatter = copyCellFormatter(settings);

    return cellFormatter(params);
  };

  getContextMenuItems = (params) => {
    const {
      colState,
      setColumnState,
      setGroupState,
      path,
      settings,
    } = this.props;

    const exportDef = {
      columnGroups: true,
      fileName: 'Accounts listing report',
      processCellCallback: exportCellFormatter(settings),
    };

    const hasSelectedNodes = params.api.getSelectedNodes().length;
    const contextMenuItems = [
      {
        name: 'New Account',
        action: () =>
          this.props.addAccount(params.node ? params.node.data.id : -1),
      },
      {
        name: 'Remove Selected Accounts',
        disabled: !params.node || !hasSelectedNodes,
        action: () => {
          params.api.getSelectedNodes().forEach((node) => {
            this.props.removeAccount(node.data.id);
          });
        },
      },
      {
        name: 'Copy',
        disabled: !params.node,
        shortcut: 'Ctrl + C',
        action: () => {
          params.api.copySelectedRangeToClipboard();
        },
      },
      {
        name: 'Unassign Account',
        disabled: !params.node || !params.node.data.groupId,
        action: () => this.props.unassignAccount(params.node.data.id),
      },
      'separator',
      {
        name: 'Select all',
        action: () => params.api.selectAll(),
      },
      {
        name: 'Deselect all',
        action: () => params.api.deselectAll(),
      },
      'separator',
      {
        name: 'Fit All Columns',
        action: () => {
          params.api.sizeColumnsToFit();
          handleGridChange(
            path,
            colState,
            setColumnState,
            setGroupState,
            ACCOUNTS_GRID,
            params.columnApi
          );
        },
      },
    ];

    if (this.props.path === GROUPS_ACCOUNTS_PATH) {
      const expandTheGroup = {
        name: 'Expand the Group',
        disabled: !params.node || !params.node.data.groupsByIdPath,
        action: () =>
          this.props.setGroupsExpanded(params.node.data.groupsByIdPath),
      };

      contextMenuItems.push(expandTheGroup);
    }

    const exportMenu = [
      'separator',
      {
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => {
              params.api.exportDataAsCsv(exportDef);
            },
          },
          {
            name: 'Excel Export',
            action: () => {
              params.api.exportDataAsExcel({ ...exportDef, exportMode: 'xml' });
            },
          },
        ],
      },
    ];
    contextMenuItems.push(...exportMenu);

    return contextMenuItems;
  };

  handleGridReady = ({ api, columnApi }) => {
    const { colState, path } = this.props;

    handleGridReady(path, colState, ACCOUNTS_GRID, api, columnApi);

    this.gridApi = api;
    this.columnApi = columnApi;
  };

  handleColumnResized = ({ columnApi, finished, source }) => {
    if (source === 'uiColumnDragged' && finished) {
      const { colState, setColumnState, setGroupState, path } = this.props;
      handleGridChange(
        path,
        colState,
        setColumnState,
        setGroupState,
        ACCOUNTS_GRID,
        columnApi
      );
    }
  };

  handleColumnMoved = ({ columnApi }) => {
    /* getColumnGroupState is ok to use if we do not reorder columns (hiding / showing is ok),
    column reordering is prevented in Grid component --> suppressMovableColumns */
    const periodIds = columnApi
      .getColumnGroupState()
      .map((cg) => cg.groupId)
      .filter((id) => id?.startsWith('period'));

    if (!isDeepEqual(this.props.periodIds, periodIds)) {
      this.props.onPeriodReorder(periodIds);
    }
  };

  handleCellValueChanged = (event) => {
    this.props.onEditAccount(rowToAccount(event.data));
  };

  handleColumnVisible = ({ columnApi }) => {
    const { colState, setColumnState, setGroupState, path } = this.props;
    handleGridChange(
      path,
      colState,
      setColumnState,
      setGroupState,
      ACCOUNTS_GRID,
      columnApi
    );
  };

  isExternalFilterPresent = () => {
    const { ALL } = accountFilters;
    return this.props.activeFilter !== ALL;
  };

  externalFilterChanged = () => {
    this.gridApi.onFilterChanged();
  };

  doesExternalFilterPass = (node) => {
    const { ASSIGNED, UNASSIGNED } = accountFilters;
    switch (this.props.activeFilter) {
      case ASSIGNED:
        return node.data.groupId !== null;
      case UNASSIGNED:
        return node.data.groupId === null;
      default:
        return true;
    }
  };

  shouldSuppressKeyboardEvent = (event) => {
    const ctrlKeysToSuppress = ['c'];

    if (event.ctrlKey) {
      return ctrlKeysToSuppress.includes(event.key);
    }
  };

  onCellKeyDown = (e) => {
    if (!e.node) {
      return;
    }
    const { event, rowIndex, api } = e;

    const ctrlKeysActions = {
      c: () => {
        api.copySelectedRangeToClipboard();
      },
    };

    if (event.ctrlKey && event.key in ctrlKeysActions) {
      e.api.setFocusedCell(rowIndex, 'description', null);
      ctrlKeysActions[event.key]();
    }
  };

  render() {
    return (
      <div
        className="accounts-grid"
        onDragStart={() =>
          this.props.draggable && this.setState({ isDragging: true })
        }
        onDragEnd={() =>
          this.props.draggable && this.setState({ isDragging: false })
        }
      >
        <Grid
          columnDefs={this.columnDefs}
          rowData={this.props.rows}
          frameworkComponents={this.frameworkComponents}
          pinnedTopRowData={this.props.checksumrow}
          rowSelection="multiple"
          rowDeselection={true}
          onGridReady={this.handleGridReady}
          onColumnResized={this.handleColumnResized}
          onDragStopped={this.handleColumnMoved}
          onCellValueChanged={this.handleCellValueChanged}
          onCellKeyDown={this.onCellKeyDown}
          suppressKeyboardEvent={({ event }) =>
            this.shouldSuppressKeyboardEvent(event)
          }
          getContextMenuItems={this.getContextMenuItems}
          enableRangeSelection={
            !(this.props.draggable && this.state.isDragging)
          }
          processCellForClipboard={this.processCellForClipboard}
          tooltipShowDelay={0}
          rowClassRules={{
            messages: (params) => params.data.messages.length > 0,
          }}
          onColumnVisible={this.handleColumnVisible}
          isExternalFilterPresent={this.isExternalFilterPresent}
          doesExternalFilterPass={this.doesExternalFilterPass}
          sideBar={this.getToolPanelItems()}
        />
        <div id="custom-dnd-ghost" />
      </div>
    );
  }
}

// TODO: https://redux.js.org/recipes/computing-derived-data
const mapStateToProps = (state) => {
  return {
    periodIds: periodIdsSelector(state),
    periodColumns: periodColumnsSelector(state, {
      editable: true,
      useGroupsPrecision: true,
      hasMostImportantCols: true,
      hasMostImportantPeriods: true,
    }),
    rows: accountRowsSelector(state),
    checksumrow: checksumRowSelector(state),
    colState: gridStateSelector(state),
    activeFilter: state.accounts.filter,
    settings: settingsSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  addAccount: (accountId) => dispatch({ type: ADD_ACCOUNT, accountId }),
  removeAccount: (accountId) => dispatch({ type: REMOVE_ACCOUNT, accountId }),
  onEditAccount: (account) => dispatch({ type: EDIT_ACCOUNT, account }),
  onPeriodReorder: (periodIds) => dispatch({ type: PERIOD_REORDER, periodIds }),
  assignAccount: (accountId, groupId) =>
    dispatch({ type: ASSIGN_ACCOUNT, accountId, groupId }),
  unassignAccount: (accountId) =>
    dispatch({ type: UNASSIGN_ACCOUNT, accountId }),
  setColumnState: (path, columnState) =>
    dispatch({
      type: SET_COLUMN_STATE,
      path,
      grid: ACCOUNTS_GRID,
      columnState,
    }),
  setGroupState: (path, groupState) =>
    dispatch({
      type: SET_GROUP_STATE,
      path,
      grid: ACCOUNTS_GRID,
      groupState,
    }),
  setGroupsExpanded: (groupIds) =>
    dispatch({
      type: SET_GROUPS_EXPANDED,
      groupIds,
      expanded: true,
      shouldSyncExpandedStates: true,
    }),
});

AccountsGrid.defaultProps = {
  path: ACCOUNTS_PATH,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountsGrid);
